/******************************************************************************
 * WorkorderSearchSelect: searchable work order picker. Instead of pasting a raw
 * work order id, the user types a reference (the most common identifier) and
 * picks a match. Reuses the shared search service (POST users/workorders/search,
 * LIKE on "ref"). Exposes the selected work order id through onChange.
 *****************************************************************************/

/* Imports */
import { useEffect, useRef, useState } from "react";
import Select, { SingleValue } from "react-select";
import { t } from "i18next";
/***/

/* Services */
import { searchWithFilters, createTextSearchQuery } from "../../../../../services/api/search";
/***/

interface WorkorderOption {
  value: string;
  label: string;
}

/**
* toOption: build a select option from a work order
* @param wo - the work order
*/
const toOption = (wo: EasyTech.Workorder): WorkorderOption => {
  const parts = [wo.ref];
  if (wo.customer?.name) parts.push(wo.customer.name);
  const label = parts.join(" — ") + (wo.status ? ` (${wo.status})` : "");
  return { value: wo.id, label };
};

/**
* WorkorderSearchSelect: search a work order by reference and select it
* @param value - selected work order id ("" when none)
* @param onChange - called with the selected work order id ("" when cleared)
* @param disabled - disable the control
* @param placeholder - input placeholder
*/
export default function WorkorderSearchSelect({
  value,
  onChange,
  disabled,
  placeholder
}: {
  value: string,
  onChange: (id: string) => void,
  disabled?: boolean,
  placeholder?: string
}) {
  const [options, setOptions] = useState<WorkorderOption[]>([]);
  const [selected, setSelected] = useState<WorkorderOption | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the visible selection when the parent resets the value (e.g. after a
  // successful action).
  useEffect(() => {
    if (!value) setSelected(null);
  }, [value]);

  /**
  * runSearch: query work orders by reference
  * @param input - the typed reference
  */
  const runSearch = (input: string) => {
    if (input.trim().length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    searchWithFilters<EasyTech.Workorder>("workorders", {
      paginate: { pageSize: 10, pageNumber: 0 },
      queries: [createTextSearchQuery(["ref"], input.trim())]
    })
      .then((ret) => setOptions(ret.data.map(toOption)))
      .finally(() => setLoading(false));
  };
  /***/

  /**
  * onInputChange: debounce the search on user typing
  * @param input - the current input value
  */
  const onInputChange = (input: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => runSearch(input), 300);
  };
  /***/

  return (
    <Select
      isClearable
      isDisabled={disabled}
      isLoading={loading}
      placeholder={placeholder}
      options={options}
      value={selected}
      // Server-side already filters on "ref"; keep every returned option.
      filterOption={() => true}
      onInputChange={(input, meta) => { if (meta.action === "input-change") onInputChange(input); }}
      onChange={(opt: SingleValue<WorkorderOption>) => {
        setSelected(opt);
        onChange(opt ? opt.value : "");
      }}
      noOptionsMessage={() => t("admin:tools.workorders.searchHint")} />
  );
}
/***/
